import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { PencilIcon } from "@heroicons/react/24/outline";
import { displayPlatformType } from "components/Environment/utils";
import {
  GetProviderName,
  GPUInstanceType,
  Location,
} from "types/InstanceTypes";
import { memoryToGiB, regionToReadable } from "components/utils";
import { InfoTooltip } from "components/Graphs/InfoTooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { getDefaultStorageGiB } from "utils/instanceTypes";
import { formatMemoryForDisplay } from "components/Environment/Create/utils";
import { formatGpuName } from "utils/gpuNames";
import { launchableDeployControlClassName } from "components/Launchables/DeployRedesign/shared/configuration/controlStyles";
import CannotStopWarning from "components/Environment/Create/ComputeSelector/RackCard/CannotStopWarning";

interface ComputeOverviewProps {
  instanceTypeObject: GPUInstanceType;
  diskStorage: string | null;
  intendedDiskStorage?: string | null;
  warningSection?: ReactNode;
  showCloudProvider?: boolean;
  summaryWarning?: ReactNode;
  cloudProviderContent?: ReactNode;
  onViewAllOptions?: () => void;
  onStorageChange?: (storage: string) => void;
  onStorageValidityChange?: (isValid: boolean) => void;
  availableLocations?: Location[];
  selectedLocation?: string | null;
  onLocationChange?: (location: string | null) => void;
}

export const ComputeOverview: React.FC<ComputeOverviewProps> = ({
  instanceTypeObject,
  diskStorage,
  intendedDiskStorage,
  warningSection,
  showCloudProvider = true,
  summaryWarning,
  cloudProviderContent,
  onViewAllOptions,
  onStorageChange,
  onStorageValidityChange,
  availableLocations = [],
  selectedLocation,
  onLocationChange,
}) => {
  const providerName = GetProviderName(instanceTypeObject) || "";

  const storageMin = (() => {
    const mins = (instanceTypeObject.supported_storage || [])
      .filter((s) => s.min_size)
      .map((s) => memoryToGiB(s.min_size!));
    return mins.length > 0 ? Math.min(...mins) : null;
  })();
  const storageMinLabel =
    storageMin !== null ? `${Math.ceil(storageMin)}GiB` : undefined;

  const storageMax = (() => {
    const maxs = (instanceTypeObject.supported_storage || [])
      .filter((s) => s.max_size)
      .map((s) => memoryToGiB(s.max_size!));
    return maxs.length > 0 ? Math.max(...maxs) : null;
  })();
  const storageMaxLabel =
    storageMax !== null ? `${Math.floor(storageMax)}GiB` : undefined;

  const getEffectiveStorage = useCallback(
    (storage: string | null) => {
      if (storage) return storage;
      if (instanceTypeObject.elastic_root_volume) {
        const defaultGiB = Number(
          getDefaultStorageGiB(instanceTypeObject.supported_storage),
        );
        return String(
          storageMin !== null ? Math.max(defaultGiB, storageMin) : defaultGiB,
        );
      }
      return "";
    },
    [
      instanceTypeObject.elastic_root_volume,
      instanceTypeObject.supported_storage,
      storageMin,
    ],
  );

  const [inputValue, setInputValue] = useState<string>(
    getEffectiveStorage(diskStorage),
  );

  const isStorageValid = useCallback(
    (value: string): boolean => {
      if (!instanceTypeObject.elastic_root_volume) return true;
      const num = Number(value);
      if (!value || isNaN(num) || num <= 0) return storageMin === null;
      if (storageMin !== null && num < storageMin) return false;
      if (storageMax !== null && num > storageMax) return false;
      return true;
    },
    [instanceTypeObject.elastic_root_volume, storageMax, storageMin],
  );

  useEffect(() => {
    const effective = getEffectiveStorage(diskStorage);
    setInputValue(effective);
    onStorageValidityChange?.(isStorageValid(effective));
    if (!diskStorage && effective) {
      onStorageChange?.(effective);
    }
  }, [
    diskStorage,
    getEffectiveStorage,
    isStorageValid,
    onStorageChange,
    onStorageValidityChange,
  ]);

  const storageErrorMessage = (() => {
    if (!instanceTypeObject.elastic_root_volume) return null;
    const num = Number(inputValue);
    const isEmpty = !inputValue || isNaN(num) || num <= 0;
    if (isEmpty) {
      if (storageMin !== null && storageMax !== null) {
        return `Value must be between ${storageMinLabel}–${storageMaxLabel}`;
      }
      if (storageMin !== null) {
        return `Value must be greater than ${storageMinLabel}`;
      }
      return null;
    }
    if (storageMin !== null && num < storageMin) {
      if (storageMax !== null) {
        return `Value must be between ${storageMinLabel}–${storageMaxLabel}`;
      }
      return `Value must be greater than ${storageMinLabel}`;
    }
    if (storageMax !== null && num > storageMax) {
      if (storageMin !== null) {
        return `Value must be between ${storageMinLabel}–${storageMaxLabel}`;
      }
      return `Value must be less than ${storageMaxLabel}`;
    }
    return null;
  })();

  const handleStorageChange = (value: string) => {
    setInputValue(value);
    const valid = isStorageValid(value);
    onStorageValidityChange?.(valid);
    if (valid) {
      onStorageChange?.(value);
    }
  };

  const gpu = instanceTypeObject.supported_gpus?.[0];
  const gpuName = gpu
    ? [gpu.count ? `${gpu.count} ×` : null, formatGpuName(gpu.name)]
        .filter(Boolean)
        .join(" ")
    : null;
  const shouldShowStorage =
    instanceTypeObject.elastic_root_volume ||
    instanceTypeObject.supported_storage?.length > 0;
  const selectedLocationDetails = availableLocations.find(
    (location) => location.name === selectedLocation,
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        {onViewAllOptions && (
          <div className="mb-2">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-xs font-semibold text-highlight shadow-sm outline-none transition-colors hover:border-highlight/60 hover:bg-zinc-800 hover:text-highlightLighter focus-visible:ring-2 focus-visible:ring-highlight"
              onClick={onViewAllOptions}
            >
              <PencilIcon className="h-4 w-4" aria-hidden="true" />
              Change compute
            </button>
          </div>
        )}
        <div className="overflow-hidden rounded-md border border-zinc-800 bg-zinc-900/30 text-primary">
          <div className="border-b border-zinc-800 bg-zinc-950/70 px-3 py-2 lg:px-4">
            <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-primary">
              {showCloudProvider && (
                <>
                  <span>{displayPlatformType(providerName || "Unknown")}</span>
                  <span className="text-zinc-600" aria-hidden="true">
                    •
                  </span>
                </>
              )}
              <span className="break-words">{instanceTypeObject.type}</span>
              {!instanceTypeObject.stoppable && (
                <CannotStopWarning alwaysColored />
              )}
            </div>
            {summaryWarning && (
              <div className="mt-1 text-xs leading-5 text-yellow-500">
                {summaryWarning}
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <dl className="min-w-full divide-y divide-zinc-800 px-3 lg:grid lg:auto-cols-[minmax(0,1fr)] lg:grid-flow-col lg:divide-y-0 lg:px-0">
              {gpuName && (
                <div className="flex min-w-0 items-center justify-between gap-4 py-2.5 lg:flex-col lg:items-start lg:justify-start lg:gap-1 lg:border-b lg:border-r lg:border-zinc-800 lg:px-4">
                  <dt className="text-xs text-secondary">GPU</dt>
                  <dd className="break-words text-right text-sm font-medium text-primary lg:text-left">
                    {gpuName}
                  </dd>
                </div>
              )}
              {gpu?.memory && (
                <div className="flex min-w-0 items-center justify-between gap-4 py-2.5 lg:flex-col lg:items-start lg:justify-start lg:gap-1 lg:border-b lg:border-r lg:border-zinc-800 lg:px-4">
                  <dt className="text-xs text-secondary">GPU memory</dt>
                  <dd className="break-words text-right text-sm font-medium text-primary lg:text-left">
                    {formatMemoryForDisplay(gpu.memory)}
                    {gpu.count > 1 ? " each" : ""}
                  </dd>
                </div>
              )}
              <div className="flex min-w-0 items-center justify-between gap-4 py-2.5 lg:flex-col lg:items-start lg:justify-start lg:gap-1 lg:border-b lg:border-r lg:border-zinc-800 lg:px-4">
                <dt className="text-xs text-secondary">vCPU</dt>
                <dd className="break-words text-right text-sm font-medium text-primary lg:text-left">
                  {instanceTypeObject.vcpu}
                </dd>
              </div>
              <div className="flex min-w-0 items-center justify-between gap-4 py-2.5 lg:flex-col lg:items-start lg:justify-start lg:gap-1 lg:border-b lg:border-r lg:border-zinc-800 lg:px-4">
                <dt className="text-xs text-secondary">Memory</dt>
                <dd className="break-words text-right text-sm font-medium text-primary lg:text-left">
                  {formatMemoryForDisplay(instanceTypeObject.memory)}
                </dd>
              </div>
              {instanceTypeObject.supported_architectures?.length > 0 && (
                <div className="flex min-w-0 items-center justify-between gap-4 py-2.5 lg:flex-col lg:items-start lg:justify-start lg:gap-1 lg:border-b lg:border-r lg:border-zinc-800 lg:px-4">
                  <dt className="text-xs text-secondary">Architecture</dt>
                  <dd className="break-words text-right text-sm font-medium text-primary lg:text-left">
                    {instanceTypeObject.supported_architectures.join(", ")}
                  </dd>
                </div>
              )}
            </dl>
          </div>
          {warningSection}
        </div>
      </div>
      {cloudProviderContent && <div>{cloudProviderContent}</div>}
      {(shouldShowStorage || onLocationChange) && (
        <div className="grid gap-4 sm:grid-cols-2">
          {onLocationChange && (
            <div>
              <div className="mb-2 flex items-center">
                <p className="text-sm font-semibold text-primary">Region</p>
                <InfoTooltip text="Regions vary across providers, so the available region values may differ depending on the selected instance type." />
              </div>
              <Select
                value={selectedLocation || "any"}
                onValueChange={(value) =>
                  onLocationChange(value === "any" ? null : value)
                }
              >
                <SelectTrigger
                  className={`${launchableDeployControlClassName} w-full px-3 py-2`}
                >
                  <SelectValue placeholder="Select a region">
                    {selectedLocation
                      ? regionToReadable[selectedLocation]
                        ? `${regionToReadable[selectedLocation].city}, ${regionToReadable[selectedLocation].country} (${selectedLocation})`
                        : selectedLocationDetails?.description ||
                          selectedLocation
                      : "Any"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {availableLocations.map((location) => {
                    const info = regionToReadable[location.name];
                    return (
                      <SelectItem key={location.name} value={location.name}>
                        {info
                          ? `${info.city}, ${info.country} (${location.name})`
                          : location.description || location.name}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
          {shouldShowStorage && (
            <div>
              <div className="mb-1 flex flex-row items-center justify-between sm:flex-col sm:items-start sm:gap-2">
                <div className="flex items-center">
                  <p className="text-sm font-semibold text-primary">Storage</p>
                  <InfoTooltip
                    text={
                      instanceTypeObject.elastic_root_volume
                        ? "Storage is billed based on the amount provisioned and continues to accrue even when your instance is stopped."
                        : "The storage amount for this instance is fixed by the cloud provider and cannot be changed."
                    }
                  />
                </div>
                {instanceTypeObject.elastic_root_volume ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      placeholder="256"
                      value={inputValue}
                      onChange={(e) => handleStorageChange(e.target.value)}
                      disabled={!onStorageChange}
                      onKeyDown={(e) => {
                        if (
                          e.metaKey ||
                          e.ctrlKey ||
                          /[0-9\b]/.test(e.key) ||
                          e.key === "Backspace" ||
                          e.key === "Delete" ||
                          e.key === "Tab" ||
                          e.key === "ArrowLeft" ||
                          e.key === "ArrowRight"
                        ) {
                          return;
                        }
                        e.preventDefault();
                      }}
                      className={`${launchableDeployControlClassName} w-24 px-3 py-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    />
                    <span className="text-sm text-secondary">GiB</span>
                  </div>
                ) : (
                  <span className="text-sm text-secondary">
                    {instanceTypeObject.supported_storage?.find((s) => s.size)
                      ?.size || "Fixed by provider"}
                  </span>
                )}
              </div>
              {storageErrorMessage && (
                <p className="mt-1 text-right text-xs text-red-500 sm:text-left">
                  {storageErrorMessage}
                </p>
              )}
              {instanceTypeObject.elastic_root_volume &&
                intendedDiskStorage &&
                isStorageValid(inputValue) &&
                Number(inputValue) < Number(intendedDiskStorage) && (
                  <p className="text-xs text-yellow-500 mt-2 text-center">
                    The original configuration uses {intendedDiskStorage} GiB.
                    Reducing storage may cause the launchable to run out of disk
                    space or behave unexpectedly.
                  </p>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
